import * as React from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { PlacementQuestion } from "../../types/PlacementQuestion";
import {
  StudentPlacementTestAnswer,
  StudentPlacementTestQuestion,
} from "../../types/PlacementTest";

interface UserPlacementTestProps {
  questions: StudentPlacementTestQuestion[];
  currentQuestionIndex: number;
  currentRemainingTime: number;
  onComplete: (answer: StudentPlacementTestAnswer) => void;
  onProgress: (answer: StudentPlacementTestAnswer) => void;
}

const UserPlacementTest: React.FC<UserPlacementTestProps> = ({
  questions,
  currentQuestionIndex,
  currentRemainingTime,
  onComplete,
  onProgress,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(currentQuestionIndex);
  const [remainingTime, setRemainingTime] =
    React.useState(currentRemainingTime);
  const [selectedAnswer, setSelectedAnswer] = React.useState("");
  // Prevent a duplicate `onComplete` if the effect re-runs while
  // `remainingTime` is still 0 (e.g. the parent re-renders for any reason).
  const submittedRef = React.useRef(false);

  React.useEffect(() => {
    if (remainingTime === 0) {
      if (submittedRef.current) return;
      submittedRef.current = true;
      const currentQuestions = questions[currentIndex];
      const answer: StudentPlacementTestAnswer = {
        _id: currentQuestions._id,
        userAnswer: selectedAnswer,
      };
      onComplete(answer);
      return;
    }

    const timer = setInterval(() => {
      setRemainingTime((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingTime, currentIndex, selectedAnswer, questions, onComplete]);

  const handleAnswerSelection = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedAnswer((event.target as HTMLInputElement).value);
  };

  const handleNextQuestion = () => {
    if (selectedAnswer) {
      const currentQuestions = questions[currentIndex];
      setSelectedAnswer("");
      const answer: StudentPlacementTestAnswer = {
        _id: currentQuestions._id,
        userAnswer: selectedAnswer,
      };
      if (currentIndex < questions.length - 1) {
        onProgress(answer);
        setCurrentIndex((prev) => prev + 1);
      } else {
        onComplete(answer);
      }
    }
  };

  return (
    <Box sx={{ bgcolor: "#f7f7f7", minHeight: "100vh" }}>
      <CssBaseline />
      <AppBar
        position="static"
        color="default"
        sx={{ backgroundColor: "#fff", mb: 6 }}
      >
        <Toolbar>
          <Typography variant="h6">Placement Test</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="subtitle1">
            {currentIndex + 1}/{questions.length}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AccessTimeIcon />
            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              {Math.floor(remainingTime / 60)
                .toString()
                .padStart(2, "0")}
              :{(remainingTime % 60).toString().padStart(2, "0")}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
          {questions[currentIndex].question}
        </Typography>
        <FormControl component="fieldset" fullWidth sx={{ mb: 4 }}>
          <FormLabel component="legend">Select an answer</FormLabel>
          <RadioGroup
            aria-label="choices"
            name="choices"
            value={selectedAnswer}
            onChange={handleAnswerSelection}
          >
            {questions[currentIndex].choices.map((choice, index) => (
              <FormControlLabel
                key={index}
                value={choice}
                control={<Radio />}
                label={`${String.fromCharCode(
                  97 + index
                ).toUpperCase()}. ${choice}`}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNextQuestion}
            disabled={!selectedAnswer}
          >
            {currentIndex === questions.length - 1
              ? "Complete Test"
              : "Next Question"}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default UserPlacementTest;
